import React from 'react';
import { Card, Grid, Space, Typography, theme } from 'antd';

const { Text } = Typography;
const { useBreakpoint } = Grid;

interface SummaryStatCardProps {
  icon: React.ReactNode;
  label: React.ReactNode;
  /** Optional control rendered on the right of the card header (e.g. an edit button). */
  extra?: React.ReactNode;
  /** Content pinned to the bottom of the card (e.g. a progress bar). */
  footer?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * KPI tile used in the top row of summary pages.
 * Cards stretch to the tallest tile in the row so the row stays aligned.
 */
export function SummaryStatCard({ icon, label, extra, footer, children }: SummaryStatCardProps) {
  const { token } = theme.useToken();
  // Two tiles share a row on phones, so they get tighter padding and spacing.
  const compact = useBreakpoint().sm === false;

  return (
    <Card
      style={{
        height: '100%',
        width: '100%',
        boxShadow: token.boxShadowTertiary,
      }}
      styles={{ body: { padding: compact ? token.paddingSM : token.paddingLG, display: 'flex', flexDirection: 'column', height: '100%' } }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: token.marginSM,
          minHeight: 28,
          marginBottom: compact ? token.marginXS : token.marginMD,
        }}
      >
        <Space size={token.marginXS} style={{ minWidth: 0 }}>
          <span
            style={{
              width: 28,
              height: 28,
              borderRadius: 999,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: token.colorPrimary,
              background: token.colorPrimaryBg,
            }}
          >
            {icon}
          </span>
          <Text type="secondary" ellipsis style={{ fontWeight: 500 }}>{label}</Text>
        </Space>
        {extra}
      </div>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: token.marginXS }}>
        {children}
      </div>
      {footer ? <div style={{ marginTop: token.marginMD }}>{footer}</div> : null}
    </Card>
  );
}

export default SummaryStatCard;
